import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import {
  useCashHandHistory,
  useCashCurrencyOptions,
} from '../../History/HistoryQueries';
import type {
  CashHandHistoryEntry,
  CashHistoryFilters,
  CurrencyOption,
} from '../../History/Type/HistoryTypes';
import {
  COLORS,
  MONO_FONT,
  CASH_PAGE_SIZE,
  CASH_FILTER_DEFAULT,
  scrollContainerSx,
  tableHeaderSx,
  tableRowSx,
  toggleSortColumn,
  type CashFilterDraft,
  type CashFilter,
  type CashResultSelection,
  type CurrencyTypeSelection,
  type SortState,
  type CashSortColumn,
} from './HistoryConstants';
import {
  formatNetAmount,
  formatGameTypeShort,
  getGameTypeColor,
  formatDateTime,
  formatAmount,
  buildCurrencyOptionValue,
  parseCurrencyOptionValue,
  formatCurrencyOptionLabel,
} from './HistoryFormatters';
import { buildCashFilters, getCashSortParams } from './HistoryFilters';
import {
  SortableHeader,
  EmptyState,
  LoadingState,
  LoadingMore,
  ErrorState,
  FilterChip,
  FilterTextField,
  FilterGrid,
  RangeField,
  FilterActions,
  FilterPanel,
  CopyIdButton,
  TableHeaderLabel,
} from './HistoryUI';
import CashHandDetailView from './CashHandDetailView';

// ─── Props ───────────────────────────────────────────────────────────────────

interface CashHistoryTabProps {
  userId: string;
  active: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

const CashHistoryTab: React.FC<CashHistoryTabProps> = ({ userId, active }) => {
  const [filterDraft, setFilterDraft] = useState<CashFilterDraft>({ ...CASH_FILTER_DEFAULT });
  const [filters, setFilters] = useState<CashHistoryFilters>(() => buildCashFilters(CASH_FILTER_DEFAULT));
  const [selectedHand, setSelectedHand] = useState<CashHandHistoryEntry | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sort, setSort] = useState<SortState<CashSortColumn>>({ column: 'date', direction: 'desc' });
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const toggleSort = (col: CashSortColumn) => setSort((prev) => toggleSortColumn(prev, col));

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!active) setSelectedHand(null);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    setFilterDraft((prev) => {
      if (prev.gameType === 'texas') {
        if (prev.currency === 'USD' && prev.currencyType === 'FIAT') return prev;
        return { ...prev, currency: 'USD', currencyType: 'FIAT' };
      }
      return prev;
    });
  }, [active, filterDraft.gameType, filterDraft.currencyType, filterDraft.currency]);

  useEffect(() => {
    if (!active || selectedHand) return;
    scrollRef.current?.scrollTo({ top: 0 });
  }, [filters, active, selectedHand]);

  // ─── Filter callbacks ────────────────────────────────────────────────────────

  const updateFilter = useCallback((patch: Partial<CashFilterDraft>) => {
    setFilterDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const applyQuickFilter = useCallback(
    (gameType: CashFilter) => {
      const next: CashFilterDraft = { ...filterDraft, gameType };
      if (gameType === 'texas') {
        next.currencyType = 'FIAT';
        next.currency = 'USD';
      } else if (gameType === 'aof') {
        next.currencyType = 'ANY';
        next.currency = '';
      } else if (gameType === 'all') {
        next.currencyType = 'ANY';
        next.currency = '';
      }
      setFilterDraft(next);
      setFilters(buildCashFilters(next));
    },
    [filterDraft]
  );

  const handleApplyFilters = useCallback(() => {
    setFilters(buildCashFilters(filterDraft));
  }, [filterDraft]);

  const handleResetFilters = useCallback(() => {
    setFilterDraft({ ...CASH_FILTER_DEFAULT });
    setFilters(buildCashFilters(CASH_FILTER_DEFAULT));
  }, []);

  // ─── Queries ─────────────────────────────────────────────────────────────────

  const cashSortParams = getCashSortParams(sort);

  const cashQuery = useCashHandHistory({
    userId,
    filters,
    pageSize: CASH_PAGE_SIZE,
    sortBy: cashSortParams.sortBy,
    sortDirection: cashSortParams.sortDirection,
    enabled: active,
  });

  const currencyOptionsQuery = useCashCurrencyOptions({
    gameType: filterDraft.gameType === 'texas' ? 'TEXAS' : undefined,
    enabled: active,
  });

  // ─── Derived data ────────────────────────────────────────────────────────────

  const cashCurrencyOptions = useMemo(() => {
    const options = currencyOptionsQuery.data ?? [];
    return [...options].sort((a, b) => a.currency.localeCompare(b.currency));
  }, [currencyOptionsQuery.data]);

  const currencyOptionsForDropdown = useMemo(() => {
    const usdFallback: CurrencyOption = { currency: 'USD', currencyType: 'FIAT', baseCurrency: 'USD' };
    const isTexas = filterDraft.gameType === 'texas';
    let options = cashCurrencyOptions;

    if (isTexas) {
      options = options.filter((o) => o.currency === 'USD');
      return options.length > 0 ? options : [usdFallback];
    }
    if (filterDraft.currencyType === 'FIAT') {
      const fiatOptions = options.filter((o) => o.currencyType === 'FIAT');
      const usdOption = fiatOptions.find((o) => o.currency === 'USD');
      if (usdOption) return [usdOption];
      return fiatOptions.length > 0 ? fiatOptions : [usdFallback];
    }
    if (filterDraft.currencyType === 'CRYPTO') {
      return options.filter((o) => o.currencyType === 'CRYPTO');
    }
    return options;
  }, [cashCurrencyOptions, filterDraft.currencyType, filterDraft.gameType]);

  const showCurrencyLoading = currencyOptionsQuery.isLoading && currencyOptionsForDropdown.length === 0;
  const showCurrencyEmpty = !currencyOptionsQuery.isLoading && currencyOptionsForDropdown.length === 0;

  const currencySelectValue = useMemo(() => {
    if (!filterDraft.currency) return '';
    const exactMatch = currencyOptionsForDropdown.find(
      (o) => o.currency === filterDraft.currency && o.currencyType === filterDraft.currencyType
    );
    if (exactMatch) return buildCurrencyOptionValue(exactMatch);
    const fallback = currencyOptionsForDropdown.find((o) => o.currency === filterDraft.currency);
    return fallback ? buildCurrencyOptionValue(fallback) : '';
  }, [currencyOptionsForDropdown, filterDraft.currency, filterDraft.currencyType]);

  const cashItems = useMemo(() => {
    return cashQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  }, [cashQuery.data]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (selectedHand) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        if (cashQuery.hasNextPage && !cashQuery.isFetchingNextPage) {
          cashQuery.fetchNextPage();
        }
      }
    },
    [cashQuery, selectedHand]
  );

  const openHandDetails = (item: CashHandHistoryEntry) => {
    if (!item.gameId) return;
    setSelectedHand(item);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  const closeHandDetails = () => setSelectedHand(null);

  // ─── Grid columns ───────────────────────────────────────────────────────────

  const cashGridColumns = isMobile
    ? '34px 1fr 50px 70px'
    : '34px 1fr 54px 120px 90px 130px 95px';

  // ─── Table header ────────────────────────────────────────────────────────────

  const renderTableHeader = () => (
    <Box sx={{ ...tableHeaderSx, gridTemplateColumns: cashGridColumns }}>
      <TableHeaderLabel label="Id" align="center" />
      <SortableHeader label="Room" active={sort.column === 'room'} direction={sort.direction} onClick={() => toggleSort('room')} />
      <SortableHeader label="Type" active={sort.column === 'type'} direction={sort.direction} onClick={() => toggleSort('type')} />
      {!isMobile && <SortableHeader label="Blinds" active={sort.column === 'blinds'} direction={sort.direction} onClick={() => toggleSort('blinds')} />}
      {!isMobile && <SortableHeader label="Pot" active={sort.column === 'pot'} direction={sort.direction} onClick={() => toggleSort('pot')} />}
      {!isMobile && <SortableHeader label="Date" active={sort.column === 'date'} direction={sort.direction} onClick={() => toggleSort('date')} />}
      <SortableHeader label="P/L" active={sort.column === 'pl'} direction={sort.direction} onClick={() => toggleSort('pl')} align="right" />
    </Box>
  );

  // ─── Cash row ────────────────────────────────────────────────────────────────

  const renderCashRow = (entry: CashHandHistoryEntry, index: number) => {
    const netAmount = formatNetAmount(entry);
    const blinds = entry.blinds;
    const blindsText =
      blinds && typeof blinds.small === 'number' && typeof blinds.big === 'number'
        ? `${formatAmount(blinds.small, entry.currency)}/${formatAmount(blinds.big, entry.currency)}`
        : null;

    const isWin = netAmount !== null && netAmount > 0;
    const isLoss = netAmount !== null && netAmount < 0;
    const amountColor = isWin ? COLORS.win : isLoss ? COLORS.loss : COLORS.textSecondary;
    const prefix = isWin ? '+' : '';

    return (
      <Box
        key={entry.gameId}
        onClick={() => openHandDetails(entry)}
        sx={{ ...tableRowSx(index), gridTemplateColumns: cashGridColumns }}
      >
        <CopyIdButton id={entry.gameId} label="game" />
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.roomName || 'Unknown room'}
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            fontFamily: 'Inter, sans-serif',
            color: getGameTypeColor(entry.gameType).color,
            bgcolor: getGameTypeColor(entry.gameType).bg,
            borderRadius: '4px',
            px: 0.75,
            lineHeight: '22px',
            textAlign: 'center',
            width: 'fit-content',
          }}
        >
          {formatGameTypeShort(entry.gameType)}
        </Box>
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>{blindsText || '\u2014'}</Typography>
        )}
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>
            {entry.pot != null ? formatAmount(entry.pot, entry.currency) : '\u2014'}
          </Typography>
        )}
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
            {formatDateTime(entry.startedAt)}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: amountColor,
            textAlign: 'right',
            fontFamily: MONO_FONT,
            letterSpacing: '-0.3px',
          }}
        >
          {typeof netAmount === 'number' ? `${prefix}${formatAmount(netAmount, entry.currency)}` : '\u2014'}
        </Typography>
      </Box>
    );
  };

  // ─── Main render ─────────────────────────────────────────────────────────────

  return (
    <>
      {!selectedHand && (
        <>
          <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            <FilterChip active={filterDraft.gameType === 'all'} onClick={() => applyQuickFilter('all')} color="#d1d1d2">
              All Games
            </FilterChip>
            <FilterChip active={filterDraft.gameType === 'texas'} onClick={() => applyQuickFilter('texas')} color="#d1d1d2">
              Texas Hold'em
            </FilterChip>
            <FilterChip active={filterDraft.gameType === 'aof'} onClick={() => applyQuickFilter('aof')} color="#d1d1d2">
              All-or-Fold
            </FilterChip>
          </Stack>
          <FilterPanel
            title="Filters"
            actions={<FilterActions onApply={handleApplyFilters} onReset={handleResetFilters} />}
            open={filterOpen}
            onToggle={() => setFilterOpen(!filterOpen)}
          >
            <FilterGrid>
              <FilterTextField
                label="Room Name"
                placeholder="Search room"
                value={filterDraft.roomNameContains}
                onChange={(e) => updateFilter({ roomNameContains: e.target.value })}
              />
              <FilterTextField
                select
                label="Result"
                value={filterDraft.result}
                onChange={(e) => updateFilter({ result: e.target.value as CashResultSelection })}
              >
                <MenuItem value="ALL">All results</MenuItem>
                <MenuItem value="WIN">Win</MenuItem>
                <MenuItem value="LOSS">Loss</MenuItem>
                <MenuItem value="PUSH">Push</MenuItem>
              </FilterTextField>
              {filterDraft.gameType !== 'texas' && (
                <FilterTextField
                  select
                  label="Currency Type"
                  value={filterDraft.currencyType}
                  onChange={(e) => {
                    const nextValue = e.target.value as CurrencyTypeSelection;
                    updateFilter({ currencyType: nextValue, currency: '' });
                  }}
                >
                  <MenuItem value="ANY">Any currency type</MenuItem>
                  <MenuItem value="FIAT">Fiat</MenuItem>
                  <MenuItem value="CRYPTO">Crypto</MenuItem>
                </FilterTextField>
              )}
              <FilterTextField
                select
                label="Currency"
                value={currencySelectValue}
                onChange={(e) => {
                  const rawValue = e.target.value as string;
                  const parsed = parseCurrencyOptionValue(rawValue);
                  if (!rawValue || !parsed) {
                    updateFilter({ currency: '', currencyType: 'ANY' });
                    return;
                  }
                  updateFilter({ currency: parsed.currency, currencyType: parsed.currencyType });
                }}
              >
                <MenuItem value="">Any currency</MenuItem>
                {showCurrencyLoading && <MenuItem value="" disabled>Loading currencies...</MenuItem>}
                {showCurrencyEmpty && <MenuItem value="" disabled>No currency options</MenuItem>}
                {currencyOptionsForDropdown.map((option) => (
                  <MenuItem key={buildCurrencyOptionValue(option)} value={buildCurrencyOptionValue(option)}>
                    {formatCurrencyOptionLabel(option)}
                  </MenuItem>
                ))}
              </FilterTextField>
              <FilterTextField
                label="Start Time From"
                type="datetime-local"
                value={filterDraft.startTimeFrom}
                onChange={(e) => updateFilter({ startTimeFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <FilterTextField
                label="Start Time To"
                type="datetime-local"
                value={filterDraft.startTimeTo}
                onChange={(e) => updateFilter({ startTimeTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <RangeField
                label="Net Result (P/L)"
                info="Your net profit or loss for each hand: balance after minus balance before."
                minValue={filterDraft.minNetAmount}
                maxValue={filterDraft.maxNetAmount}
                onMinChange={(v) => updateFilter({ minNetAmount: v })}
                onMaxChange={(v) => updateFilter({ maxNetAmount: v })}
              />
              <RangeField
                label="Small Blind"
                minValue={filterDraft.minSmallBlind}
                maxValue={filterDraft.maxSmallBlind}
                onMinChange={(v) => updateFilter({ minSmallBlind: v })}
                onMaxChange={(v) => updateFilter({ maxSmallBlind: v })}
              />
              <RangeField
                label="Big Blind"
                minValue={filterDraft.minBigBlind}
                maxValue={filterDraft.maxBigBlind}
                onMinChange={(v) => updateFilter({ minBigBlind: v })}
                onMaxChange={(v) => updateFilter({ maxBigBlind: v })}
              />
              <RangeField
                label="Ante"
                minValue={filterDraft.minAnte}
                maxValue={filterDraft.maxAnte}
                onMinChange={(v) => updateFilter({ minAnte: v })}
                onMaxChange={(v) => updateFilter({ maxAnte: v })}
              />
            </FilterGrid>
          </FilterPanel>
        </>
      )}

      <Box sx={{ height: '1px', background: COLORS.gradientDivider, mb: 1.5 }} />

      <Box ref={scrollRef} onScroll={handleScroll} sx={scrollContainerSx}>
        {selectedHand ? (
          <CashHandDetailView
            userId={userId}
            gameId={selectedHand.gameId}
            roomName={selectedHand.roomName}
            startedAt={selectedHand.startedAt}
            summary={selectedHand}
            onBack={closeHandDetails}
            active={active}
          />
        ) : (
          <>
            {cashQuery.isLoading && <LoadingState />}
            {cashQuery.error && <ErrorState />}
            {!cashQuery.isLoading && !cashQuery.error && cashItems.length === 0 && (
              <EmptyState message="No cash game hands yet. Play some hands and they'll appear here!" />
            )}
            {!cashQuery.isLoading && !cashQuery.error && cashItems.length > 0 && (
              <>
                {renderTableHeader()}
                <Box sx={{ mt: 0.5 }}>
                  {cashItems.map((item, i) => renderCashRow(item, i))}
                </Box>
              </>
            )}
            {cashQuery.isFetchingNextPage && <LoadingMore />}
          </>
        )}
      </Box>
    </>
  );
};

export default CashHistoryTab;
